type Token = string;
type Tokens = {
  peek: () => undefined | Token;
  consume: () => undefined | Token;
};

type Operator = "+" | "-" | "*" | "/";
type AST =
  | { t: "operator"; kind: Operator; left: AST; right: AST }
  | { t: "negate"; value: AST }
  | { t: "literal"; value: number };
type Parser = (tokens: Tokens) => AST | null;

function tokenize(input: string): Tokens {
  input = input.replace(/,/g, "");
  let current: Token | undefined;
  const regex = /\s*(\d+\.?\d*|\.\d+|\S)\s*/g;
  const consume = () => {
    const oldCurrent = current;
    const match = regex.exec(input);
    current = match?.[1];
    return oldCurrent;
  };
  consume(); // Prime the lexer with its first token
  return { peek: () => current, consume };
}

function parseLeafExpression(tokens: Tokens): AST | null {
  const token = tokens.peek();
  if (/^\.?\d/.test(token || "")) {
    tokens.consume();
    return { t: "literal", value: Number(token) };
  } else if (token === "-") {
    tokens.consume();
    const value = parseLeafExpression(tokens);
    return value && { t: "negate", value };
  } else if (token === "(") {
    tokens.consume(); // toss the open parens
    const expr = parseAddExpression(tokens);
    // toss the closing parens
    return tokens.consume() === ")" ? expr : null;
  }
  return null;
}

function makeOperatorParser(operators: Operator[], operandParser: Parser): Parser {
  return (tokens) => {
    let left = operandParser(tokens);
    if (!left) {
      return null;
    }
    let token = tokens.peek();
    while (token && operators.includes(token)) {
      tokens.consume();
      const right = operandParser(tokens);
      if (!right) {
        return null;
      }
      left = { t: "operator", kind: token as Operator, left, right };
      token = tokens.peek();
    }
    return left;
  };
}

const parseMulExpression = makeOperatorParser(["*", "/"], parseLeafExpression);
const parseAddExpression = makeOperatorParser(["+", "-"], parseMulExpression);

function evaluate(ast: AST): number {
  switch (ast.t) {
    case "operator": {
      const left = evaluate(ast.left);
      const right = evaluate(ast.right);
      switch (ast.kind) {
        case "+":
          return left + right;
        case "-":
          return left - right;
        case "*":
          return left * right;
        case "/":
          return right === 0 ? 0 : left / right;
      }
    }
    // eslint-disable-next-line no-fallthrough
    case "negate":
      return -1 * evaluate(ast.value);
    case "literal":
      return ast.value;
  }
}

/** Evaluates an arithmetic money expression like `120+45.50` or `3*40`. Returns null when the
 *  input doesn't parse; `isExpression` is false for a plain number. */
export function evaluateEquation(input: string): { value: number; isExpression: boolean } | null {
  const tokens = tokenize(input);
  const ast = parseAddExpression(tokens);
  if (!ast || tokens.peek()) return null;
  return { value: evaluate(ast), isExpression: ast.t !== "literal" };
}
